import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Save, X, BookOpen } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminCoachingToolsPage = () => {
  const [masterPassword, setMasterPassword] = useState('');
  const [tools, setTools] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    domain: '',
    title: '',
    description: '',
    content: '',
    isPremium: true
  });

  const domains = [
    'Career & Vocation',
    'Financial Well-Being',
    'Physical Health',
    'Emotional Well-Being',
    'Family',
    'Friends',
    'Relationships & Love',
    'Community & Giving',
    'Fun & Recreation',
    'Physical Environment',
    'Personal Growth',
    'Spirituality'
  ];

  const handleLogin = async () => {
    if (!masterPassword) {
      toast({
        title: 'Password Required',
        description: 'Please enter the master password',
        variant: 'destructive',
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/admin/coaching-tools/list`,
        {
          params: { master_password: masterPassword },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTools(response.data.tools);
      setIsLoggedIn(true);
      toast({
        title: 'Access Granted',
        description: 'Welcome to Coaching Tools Manager',
      });
    } catch (error) {
      toast({
        title: 'Access Denied',
        description: error.response?.data?.detail || 'Invalid password',
        variant: 'destructive',
      });
    }
  };

  const handleCreateTool = async () => {
    if (!formData.domain || !formData.title || !formData.description) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/admin/coaching-tools/create`,
        formData,
        {
          params: { master_password: masterPassword },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh list
      const response = await axios.get(
        `${BACKEND_URL}/admin/coaching-tools/list`,
        {
          params: { master_password: masterPassword },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTools(response.data.tools);
      setShowCreateForm(false);
      setFormData({
        domain: '',
        title: '',
        description: '',
        content: '',
        isPremium: true
      });

      toast({
        title: 'Success!',
        description: 'Coaching tool created successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Could not create tool',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTool = async (toolId) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/admin/coaching-tools/update/${toolId}`,
        formData,
        {
          params: { master_password: masterPassword },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh list
      const response = await axios.get(
        `${BACKEND_URL}/admin/coaching-tools/list`,
        {
          params: { master_password: masterPassword },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTools(response.data.tools);
      setEditingTool(null);

      toast({
        title: 'Success!',
        description: 'Coaching tool updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Could not update tool',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (!window.confirm('Are you sure you want to delete this tool?')) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/admin/coaching-tools/delete/${toolId}`,
        {
          params: { master_password: masterPassword },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTools(tools.filter(t => t.id !== toolId));

      toast({
        title: 'Success!',
        description: 'Coaching tool deleted successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Could not delete tool',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (tool) => {
    setEditingTool(tool.id);
    setFormData({
      domain: tool.domain,
      title: tool.title,
      description: tool.description,
      content: tool.content || '',
      isPremium: tool.isPremium
    });
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center px-4 py-12">
        <Card className="max-w-md w-full p-8 bg-gray-800 border-2 border-green-500">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-gray-900" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Coaching Tools Manager</h1>
            <p className="text-gray-400">Admin Access Required</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-gray-300">Master Password</Label>
              <Input
                id="password"
                type="password"
                value={masterPassword}
                onChange={(e) => setMasterPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="Enter master password"
              />
            </div>

            <Button
              onClick={handleLogin}
              className="w-full bg-green-500 hover:bg-green-600 text-gray-900 font-bold py-6"
            >
              Access Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Coaching Tools Manager</h1>
            <p className="text-gray-600">Create and manage your coaching resources</p>
          </div>
          <Button
            onClick={() => {
              setShowCreateForm(true);
              setFormData({
                domain: '',
                title: '',
                description: '',
                content: '',
                isPremium: true
              });
            }}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create New Tool
          </Button>
        </div>

        {/* Create/Edit Form */}
        {(showCreateForm || editingTool) && (
          <Card className="p-6 mb-8 bg-white shadow-xl border-2 border-green-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingTool ? 'Edit Tool' : 'Create New Tool'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingTool(null);
                }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Domain *</Label>
                <select
                  value={formData.domain}
                  onChange={(e) => setFormData({...formData, domain: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select a domain</option>
                  {domains.map(domain => (
                    <option key={domain} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="E.g., Career Vision Board"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of what this tool does..."
                  className="mt-1 min-h-[80px]"
                />
              </div>

              <div>
                <Label>Content (Optional)</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Detailed instructions, exercises, or worksheets..."
                  className="mt-1 min-h-[150px]"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.isPremium}
                  onChange={(e) => setFormData({...formData, isPremium: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label>Premium Only</Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => editingTool ? handleUpdateTool(editingTool) : handleCreateTool()}
                  disabled={isLoading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingTool ? 'Update Tool' : 'Create Tool'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingTool(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Tools List */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool) => (
            <Card key={tool.id} className="p-6 hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                    {tool.domain}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1">{tool.title}</h3>
                </div>
                {tool.isPremium && (
                  <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full font-semibold">
                    Premium
                  </span>
                )}
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-3">{tool.description}</p>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startEdit(tool)}
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteTool(tool.id)}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {tools.length === 0 && (
          <Card className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Tools Yet</h3>
            <p className="text-gray-600 mb-6">Create your first coaching tool to get started</p>
            <Button
              onClick={() => setShowCreateForm(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create First Tool
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminCoachingToolsPage;
